import React, {useEffect, useMemo, useState, useRef} from 'react'
import {useToast} from '../../hooks/use-toast'
import {Box} from '@chakra-ui/react'
import {Helmet} from 'react-helmet'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getColorBarConfigs} from '../../utils/product-utils'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {getTotalQuantity} from '../../utils/basket-utils'
import {useGlobaleContext} from '../../contexts/globale'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useTagManager from '../../hooks/use-tag-manager'
import {
    API_ERROR_MESSAGE,
    BASKET_URL,
    MAX_CACHE_AGE,
    PD_COLORBAR_PDP,
    STALE_WHILE_REVALIDATE
} from '../../constants'
import {
    useAccessToken,
    useCommerceApi,
    useShopperBasketsMutationHelper,
    useCustomerType,
    useCustomerOrders,
    useCustomerId
} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import LoadingSpinner from '../../components/loading-spinner'
import {useQueryClient} from '@tanstack/react-query'
import usePageDesigner from '../../hooks/use-page-designer'
import EspSkeleton from '../home/partials/skeleton'
import PDComponent from '../../components/page-designer/pd-component'
import useFluidConfigureDetails from '../../hooks/use-fluid-configure-details'
import {defineMessage, useIntl} from 'react-intl'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import CbDesktopSpinner from './partials/cb-desktop-spinner'
import CbMobileSpinner from './partials/cb-mobile-spinner'
import {Link as RouteLink, useLocation} from 'react-router-dom'
import {
    Breadcrumb as ChakraBreadcrumb,
    BreadcrumbItem as ChakraBreadcrumbItem,
    BreadcrumbLink as ChakraBreadcrumbLink,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useMonetate from '../../hooks/use-monetate'
import {useWishList} from '../../hooks/use-wish-list'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {logBasketErrorToSsr} from '../../utils/log-basket-error-to-ssr'

const COLORBAR_SELECTION_UNAVAILABLE_ERROR_MESSAGE = defineMessage({
    id: 'cb.pdp.error.selection_no_longer_available',
    defaultMessage: 'This selection is no longer available'
})

const isProductItemNotAvailableError = (errResponse) =>
    Boolean(errResponse?.type?.includes('product-item-not-available'))

const getAddToCartErrorMessage = (errResponse) =>
    isProductItemNotAvailableError(errResponse)
        ? COLORBAR_SELECTION_UNAVAILABLE_ERROR_MESSAGE
        : API_ERROR_MESSAGE

/**
 * PDP component for ColorBar product customization (Fluid Configure integration)
 * Handles configurator script injection, dynamic product setup, and
 * basket addition for configured ColorBar items.
 */
const ColorBarPDP = () => {
    const {res} = useServerContext()
    const api = useCommerceApi()
    const toast = useToast()
    const {formatMessage} = useIntl()
    const {site} = useMultiSite()
    const {getTokenWhenReady} = useAccessToken()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const [actionSpinner, setActionSpinner] = useState(false)
    const queryClient = useQueryClient()
    const {data: cbConfig} = useFluidConfigureDetails()
    const tagManager = useTagManager()
    const {countryISO, currencyCode, globaleCurrency} = useGlobaleContext()
    const location = useLocation()
    const breadcrumbStyles = useStyleConfig('Breadcrumb')
    const monetate = useMonetate()
    const [productId, setProductId] = useState(null)
    const pdpRef = useRef()
    const breadcrumbRef = useRef(null)
    const customerClientId = useCustomerId()

    const colorBarCategory = useMemo(() => {
        const searchParams = new URLSearchParams(location.search)
        return searchParams.get('cbCategory') || null
    }, [location.search])

    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: !onClient()
        })
    const {isRegistered} = useCustomerType()

    // Extract client config from Commerce API
    const {clientConfig} = api.shopperExperience
    const {
        app: {commerceAPI}
    } = getConfig()

    // Extract Fluid Configure API settings (from site preferences)
    const {fluidConfigure, storefront} = commerceAPI || {}
    const {config} = fluidConfigure || {}
    const {hostUrl} = storefront || {}

    useEffect(() => {
        if (!window.CBContent) {
            try {
                window.CBContent = JSON.parse(cbConfig.colorBarConfiguration)
            } catch (e) {
                console.log(e.message)
            }
        }
        const params = new URLSearchParams(window.location.search)
        setProductId(params.get('product'))
    }, [])

    let apiKey, demandwareProductID, configureUiJs, configureHtmlJs, customerId

    // Parse Fluid Configure config safely
    try {
        if (config) {
            const parsedConfig = JSON.parse(config)
            apiKey = parsedConfig.apiKey
            demandwareProductID = parsedConfig.demandwareProductID
            configureHtmlJs = parsedConfig.configureHtmlJs
            customerId = parsedConfig.customerId
            configureUiJs = parsedConfig.configureUiJs
        }
    } catch (error) {
        console.error(error.message)
    }

    // Set CDN cache headers for better performance and SWR support
    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    //fetch metal SKU to check quantity for stone
    const fetchProduct = async (metalSKU) => {
        const token = await getTokenWhenReady()
        const fetchedProduct = await api.shopperProducts.getProduct({
            parameters: {id: metalSKU},
            headers: {Authorization: `Bearer ${token}`}
        })
        return fetchedProduct
    }

    // fetch the fresh basket
    const fetchLiveBasket = async () => {
        const token = await getTokenWhenReady()
        const liveBasket = await api.shopperBaskets.getBasket({
            parameters: {basketId: window.basketId},
            headers: {Authorization: `Bearer ${token}`}
        })
        return liveBasket
    }

    // update the quantity if stone or metal is already in the basket
    const updateItemsInBasket = async (body) => {
        const token = await getTokenWhenReady()
        await api.shopperBaskets.updateItemsInBasket({
            parameters: {basketId: localStorage.getItem('basketId')},
            body: body,
            headers: {Authorization: `Bearer ${token}`}
        })
    }

    useEffect(() => {
        fetchProduct()
    }, [])

    /**
     * addToCart()
     * --------------------------------
     * Builds a filtered list of basket items (metal, stones, master product)
     * from the provided configurator data and posts them to Commerce Cloud.
     */
    const addToCart = async (configs, recipe) => {
        const filteredItems = []
        const metalProduct = await fetchProduct(configs.fluidConfigureMetalSKU)

        // 1. Add metal SKU (base product)
        if (configs.fluidConfigureMetalSKU) {
            filteredItems.push({
                productId: configs.fluidConfigureMetalSKU,
                price: 0,
                c_isColorbarComponent: true,
                c_hiddenFluidProduct: true,
                c_fluidConfigureRecipeID: configs.fluidConfigureRecipeID,
                quantity: Number(configs.fluidConfigureQuantity) || 1
            })
        }

        // 2. Add stone components (if available)
        if (configs.fluidConfigureStones) {
            try {
                const stones = JSON.parse(configs.fluidConfigureStones)

                let pushedStones = {}
                let index = 1 //start index at 1 as index 0 is the metal frame

                stones.forEach((stoneId) => {
                    let stoneQty = metalProduct?.c_cbIsComponentSet ? 2 : 1 // component sets are usually earrings so add 2 stones

                    if (stoneId != 'NoOptionalStone') {
                        if (pushedStones?.[stoneId]) {
                            stoneQty = pushedStones[stoneId].qty
                                ? stoneQty + pushedStones[stoneId].qty
                                : stoneQty
                            pushedStones[stoneId].qty =
                                stoneQty * (configs?.fluidConfigureQuantity || 1)

                            // update existing item quantity
                            filteredItems[pushedStones[stoneId].index].quantity = stoneQty
                        } else {
                            pushedStones[stoneId] = {qty: stoneQty, index: index}
                            filteredItems.push({
                                productId: stoneId,
                                price: 0,
                                quantity:
                                    (Number(stoneQty) || 1) *
                                    (configs?.fluidConfigureQuantity || 1),
                                c_fluidConfigureRecipeID: configs.fluidConfigureRecipeID,
                                c_hiddenFluidProduct: true,
                                c_isColorbarComponent: true,
                                c_orderHandleType: []
                            })
                        }
                    }
                    index++
                })
            } catch (error) {
                console.error(error.message)
            }
        }

        // 3. Add master ColorBar product (configurable parent)
        filteredItems.push({
            productId: configs.pid,
            price: configs.fluidConfigurePrice,
            quantity: Number(configs.fluidConfigureQuantity) || 1,
            c_fluidConfigureData: JSON.stringify({
                fluidConfigurePrice: configs.fluidConfigurePrice,
                fluidConfigureQuantity: configs.fluidConfigureQuantity,
                fluidConfigureRecipeID: configs.fluidConfigureRecipeID,
                fluidConfigureResourceURL: configs.fluidConfigureResourceURL,
                fluidConfigureImageURL: configs.fluidConfigureImageURL,
                format: configs.format,
                fluidProductDependense: configs.fluidProductDependense,
                fluidConfigureMetalSKU: configs.fluidConfigureMetalSKU,
                fluidConfigureStones: configs.fluidConfigureStones,
                fluidConfigureMetadata: configs.fluidConfigureMetadata,
                fluidConfigureProductID: configs.fluidConfigureProductID,
                hasPhoto: configs.hasPhoto,
                productName: configs.productName,
                colorbarCategory: configs.colorbarCategory,
                finishedsku: configs.finishedsku,
                comingFromColorbar: configs.comingFromColorbar
            }),
            c_analyticsAttributes: JSON.stringify({
                listID: 'pdp',
                listName: 'pdp'
            })
        })

        /** Fetch the fresh basket. **/
        const liveBasket = window.basketId ? await fetchLiveBasket() : null
        const basketMap = new Map(liveBasket?.productItems?.map((x) => [x.productId, x]))
        const existingComponents = []

        /** Do not add stones and metals if they are already in the basket. **/
        const cleanedFilteredItems = filteredItems?.filter((x) =>
            basketMap.has(x.productId) && x.c_isColorbarComponent
                ? (existingComponents.push({
                      productId: x.productId,
                      itemId: basketMap.get(x.productId).itemId,
                      quantity: Number(basketMap.get(x.productId).quantity) + Number(x.quantity)
                  }),
                  0)
                : 1
        )
        // 4. Add items to basket (new or existing)
        try {
            if (existingComponents?.length) {
                /** If a metal or stone is already added in the basket, increase its quantity **/
                await updateItemsInBasket(existingComponents)
            }
            let response
            if (window.basketId) {
                // Add to existing basket
                const token = await getTokenWhenReady()
                response = await fetch(
                    `${clientConfig.proxy}/checkout/shopper-baskets/v1/organizations/${clientConfig.parameters.organizationId}/baskets/${window.basketId}/items?siteId=${site?.id}`,
                    {
                        method: 'POST',
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(cleanedFilteredItems)
                    }
                )
                if (response.ok) {
                    await queryClient.invalidateQueries()
                } else {
                    setActionSpinner(false)
                    let errResponse = null
                    try {
                        errResponse = await response.json()
                    } catch (e) {
                        console.log(e.message)
                    }
                    toast({
                        title: formatMessage(getAddToCartErrorMessage(errResponse)),
                        status: 'error'
                    })

                    return
                }
            } else {
                // Create new basket and add items
                response = await addItemToNewOrExistingBasket(cleanedFilteredItems)
            }

            tagManager.addToCart(recipe, configs, null, null, null, null, null, null, true)
            const addToCartData = {
                itemsAdded: [recipe],
                variant: configs,
                method: 'pdp',
                currency: globaleCurrency,
                sorting: undefined,
                indexPosition: undefined,
                isColorBar: true,
                listName: 'pdp'
            }
            tagManager.addToCartGA4(addToCartData)
            setActionSpinner(false)
            window.location.href = `${hostUrl}${BASKET_URL}?cartid=${
                response?.basketId || window.basketId
            }`
        } catch (error) {
            setActionSpinner(false)
            const errResponse = error?.response ? await error.response.json() : null
            logger.error('ATCXL Error adding colorbar product to basket', {
                namespace: 'ColorBarPDP.addToCart',
                additionalProperties: {error, errResponse, basketId: basket?.basketId}
            })
            logBasketErrorToSsr('ColorBarPDP.addToCart', {
                error,
                errResponse,
                basketId: basket?.basketId
            })
            toast({
                title: formatMessage(getAddToCartErrorMessage(errResponse)),
                status: 'error'
            })
            console.error(error.message)
        }
    }

    /**
     * useEffect()
     * --------------------------------
     * Exposes `window.configureAddToCart()` for the external
     * Fluid Configure script to trigger basket updates.
     */
    useEffect(() => {
        window.configureAddToCart = (recipe) => {
            const configs = getColorBarConfigs(recipe, apiKey, demandwareProductID, customerId)
            setActionSpinner(true)
            addToCart(configs, recipe)
        }

        // Cleanup handler when component unmounts
        return () => {
            delete window.configureAddToCart
        }
    }, [apiKey, demandwareProductID])

    const pageId = PD_COLORBAR_PDP
    const {data: page, isLoading} = usePageDesigner({pageId: pageId}, {enabled: true})
    let swatchLoaded = false

    const setMonetateSwatch = (key, name) => {
        if (localStorage.getItem('monetateSwatch-' + key)) {
            localStorage.setItem(
                'monetateSwatch-' + key,
                localStorage.getItem('monetateSwatch-' + key) + ',' + name
            )
        } else {
            localStorage.setItem('monetateSwatch-' + key, name)
        }
    }

    useEffect(() => {
        /** Not a reactJS practice - We need to deal with the clicks of ColorBar Swatch **/
        const observer = new MutationObserver(() => {
            const target = document.querySelector('.configure-container-right')
            if (target && !swatchLoaded) {
                swatchLoaded = true
                document.addEventListener('click', (e) => {
                    if (e.target?.classList?.contains('fc-swatch')) {
                        let itemColor = e.target.getAttribute('aria-label')
                        let parentNode = e.target.closest('.fc-ca-fieldset')
                        let itemType

                        const selectedSwatch = e.target.closest('.fc-swatch-selected')

                        if (selectedSwatch) {
                            setProductId(selectedSwatch.dataset.value)
                        }

                        if (parentNode.querySelector('.fc-ca-legend')) {
                            itemType = parentNode
                                .querySelector('.fc-ca-legend')
                                .textContent.split(' ')
                            if (itemType[0].toLowerCase() == 'metal') {
                                tagManager.colorBarMetalStoneSelect('Metal: ' + itemColor)
                                setMonetateSwatch('metal', itemColor)
                            } else {
                                tagManager.colorBarMetalStoneSelect('Stone: ' + itemColor)
                                setMonetateSwatch('color', itemColor)
                            }
                        } else {
                            tagManager.colorBarMetalStoneSelect('Stone: ' + itemColor)
                            setMonetateSwatch('color', itemColor)
                        }
                    }
                })
            }
        })

        observer.observe(document.body, {
            childList: true,
            subtree: true
        })

        return () => observer.disconnect()
    }, [])

    const {changeUp} = commerceAPI
    const {configChangeup} = changeUp || {}

    var changeUpProductID = null

    try {
        const jsonConfig = JSON.parse(configChangeup)
        changeUpProductID = jsonConfig.enabled ? jsonConfig.productID : null
    } catch (e) {
        console.log(e.message)
    }

    const [globalDataLoaded, setGlobalDataLayerLoaded] = useState(false)
    const [dataLayerLoaded, setDataLayerLoaded] = useState(false)
    const debounceRef = useRef(null)

    const {data: customer} = useCurrentCustomer()
    const {isLoading: isBasketLoading, data: basket} = useCurrentBasket()
    const totalQuantity = getTotalQuantity(basket, changeUpProductID)
    const {data: wishlist, isLoading: isWishlistLoading} = useWishList()

    useEffect(() => {
        const city = cookie.get('city')
        const country = cookie.get('countryCode') || 'US'
        const state = cookie.get('state')
        if (!country) return

        if (
            ((customer?.customerType === 'registered' && customer?.email) ||
                customer?.customerType === 'guest') &&
            !isBasketLoading &&
            !globalDataLoaded
        ) {
            if (debounceRef.current) clearTimeout(debounceRef.current)

            debounceRef.current = setTimeout(() => {
                tagManager.globalDataLoad(
                    'PDP',
                    null,
                    totalQuantity,
                    basket?.productTotal,
                    customer,
                    city,
                    country,
                    state
                )
                setGlobalDataLayerLoaded(true)
            }, 2000)
        }

        if (
            (customer?.customerType === 'registered' && customer?.email) ||
            customer?.customerType === 'guest'
        ) {
            pdpRef.current?.setAttribute(
                'data-is-registered',
                customer?.customerType === 'registered'
            )
        }
    }, [customer?.customerType, customer?.email, isBasketLoading, cookie])

    useEffect(() => {
        if (dataLayerLoaded) {
            tagManager.productView({}, null, false, true, false)
        }
    }, [dataLayerLoaded])

    useEffect(() => {
        setGlobalDataLayerLoaded(false)
        setDataLayerLoaded(false)
    }, [])

    useEffect(() => {
        // 'CookieStorage' takes some miliseconds to fetch cookies, that's why we've used 'useEffect'
        const city = cookie.get('city')
        const country = cookie.get('countryCode') || 'US'
        const state = cookie.get('state')
        if (
            //product?.name &&
            globalDataLoaded &&
            ((customer?.customerType === 'registered' && customer?.email) ||
                customer?.customerType === 'guest')
        ) {
            var breadCrumb = ''
            try {
                tagManager.dataLayerLoad(
                    'PDP',
                    isRegistered,
                    breadCrumb,
                    countryISO,
                    currencyCode,
                    customer,
                    city,
                    country,
                    state,
                    null,
                    null,
                    'other'
                )
            } catch (e) {
                tagManager.dataLayerLoad(
                    'PDP',
                    isRegistered,
                    breadCrumb,
                    null,
                    null,
                    customer,
                    city,
                    country,
                    state,
                    null,
                    null,
                    'other'
                )
            }
            setDataLayerLoaded(true)
        }
    }, [globalDataLoaded, customer?.customerType, customer?.email])

    const orderSearchParams = useSearchParams({limit: 1, offset: 0})
    const {limit, offset} = orderSearchParams[0]
    const {data: {data: orders} = {}, isLoading: isOrdersLoading} = useCustomerOrders(
        {
            parameters: {customerId: customerClientId, limit, offset}
        },
        {enabled: onClient() && !!customerClientId && customer?.customerType === 'registered'}
    )

    useEffect(() => {
        if (!isOrdersLoading && pdpRef) {
            window.lastOrderDate = orders?.[0]?.creationDate || 'n/a'
            pdpRef.current?.setAttribute('data-last-order-date', orders?.[0]?.creationDate || 'n/a')
        }
    }, [isOrdersLoading, pdpRef])

    useEffect(() => {
        if (!isWishlistLoading && pdpRef) {
            pdpRef.current?.setAttribute(
                'data-wishlist-length',
                wishlist?.customerProductListItems?.length || 0
            )
        }
    }, [isWishlistLoading, pdpRef])

    useEffect(() => {
        if (
            (customer?.customerType === 'registered' && customer?.email) ||
            customer?.customerType === 'guest'
        ) {
            pdpRef.current?.setAttribute(
                'data-is-registered',
                customer?.customerType === 'registered'
            )
            window.birthdayMonth = customer?.c_birthdayMonth
            window.monetatePhilantropyShopper = customer?.c_monetatePhilantropyShopper
        }
    }, [customer?.customerType, customer?.email, pdpRef])

    useEffect(() => {
        if (!isOrdersLoading && pdpRef) {
            window.lastOrderDate = orders?.[0]?.creationDate || 'n/a'
            pdpRef.current?.setAttribute('data-last-order-date', orders?.[0]?.creationDate || 'n/a')
        }
    }, [isOrdersLoading, pdpRef])

    useEffect(() => {
        if (!isBasketLoading && pdpRef) {
            pdpRef.current?.setAttribute(
                'data-basket-items-length',
                basket?.productItems?.length ? JSON.stringify(basket?.productItems) : '[]'
            )
            pdpRef.current?.setAttribute(
                'data-basket-gift-length',
                basket?.giftCertificateItems?.length
                    ? JSON.stringify(basket?.giftCertificateItems)
                    : '[]'
            )
        }
    }, [isBasketLoading, pdpRef, basket])

    const triggerMonetateEvent = () => {
        const idString = 'product'
        const links = breadcrumbRef.current ? breadcrumbRef.current.querySelectorAll('a') : []
        const basketLength = pdpRef.current?.getAttribute('data-basket-items-length')
            ? JSON.parse(pdpRef.current?.getAttribute('data-basket-items-length'))
            : []
        const isCustomerRegistered = pdpRef.current?.getAttribute('data-is-registered') === 'true'
        const wishlistLength = pdpRef.current?.getAttribute('data-wishlist-length') || 0
        const basketGiftLength = pdpRef.current?.getAttribute('data-basket-gift-length')
            ? JSON.parse(pdpRef.current?.getAttribute('data-basket-gift-length'))
            : []

        const product = {
            productId: demandwareProductID,
            id: productId
        }

        const cbElemProductName = document.querySelector('.fc-product-name')

        const breadcrumb = [
            'Home',
            ...Array.from(links).map((link) => link.textContent.trim()),
            cbElemProductName ? cbElemProductName.textContent : ''
        ]

        if (breadcrumb) {
            window.mtBreadcrumb = breadcrumb
            monetate.monetateTrackPage(
                idString,
                breadcrumb,
                basketLength,
                isCustomerRegistered,
                wishlistLength,
                [product],
                null,
                null,
                basketGiftLength
            )
            window.monetateRef = productId
        }
    }

    useEffect(() => {
        if (!productId) return

        const intervalId = setInterval(() => {
            const el = pdpRef.current

            const isReady =
                (el?.getAttribute('data-last-order-date') ||
                    customer?.customerType !== 'registered') &&
                (el?.getAttribute('data-wishlist-length') ||
                    customer?.customerType !== 'registered') &&
                el?.getAttribute('data-basket-items-length') !== '' &&
                el?.getAttribute('data-is-registered')

            if (isReady) {
                triggerMonetateEvent()
                clearInterval(intervalId) // stop once done
            }
        }, 1000)

        // 🔑 cleanup runs on dependency change or unmount
        return () => {
            clearInterval(intervalId)
        }
    }, [productId, customer])

    /**
     * Renders the Fluid Configure target container and script tags.
     * Helmet ensures scripts and preload links are injected into <head>.
     */
    return (
        <Box
            ref={pdpRef}
            data-testid="color-bar-pdp"
            data-wishlist-length=""
            data-last-order-date=""
            data-basket-items-length=""
            data-basket-gift-length=""
            data-is-registered=""
            backgroundColor="gray.100"
            sx={{
                '.sf-breadcrumb': {
                    marginInlineStart: {base: '10px', lg: '40px'},
                    marginTop: '10px',
                    marginBottom: '30px'
                }
            }}
        >
            {colorBarCategory && (
                <ChakraBreadcrumb
                    className="sf-breadcrumb"
                    ref={breadcrumbRef}
                    {...breadcrumbStyles.container}
                    separator="/"
                >
                    <ChakraBreadcrumbItem>
                        <ChakraBreadcrumbLink
                            as={RouteLink}
                            to="/colorbarlanding.html"
                            sx={breadcrumbStyles.link}
                        >
                            Color Bar®
                        </ChakraBreadcrumbLink>
                    </ChakraBreadcrumbItem>
                    <ChakraBreadcrumbItem className="breadcrumb-item">
                        <ChakraBreadcrumbLink
                            as={RouteLink}
                            to={`/custom-jewelry.html?category=${encodeURIComponent(
                                colorBarCategory
                            )}`}
                            sx={breadcrumbStyles.link}
                        >
                            {colorBarCategory}
                        </ChakraBreadcrumbLink>
                    </ChakraBreadcrumbItem>
                </ChakraBreadcrumb>
            )}
            {actionSpinner && <LoadingSpinner position="fixed" top="auto" />}
            <Box
                id="fluidConfigure"
                minHeight={{base: '600px', lg: '800px'}}
                sx={{
                    '.fc-back-to-style': {
                        display: 'none'
                    },
                    '.fc-attribute-header-name': {
                        fontSize: '23px'
                    },
                    '.fc-attribute-header-swatch-name': {
                        fontSize: '16px',
                        fontWeight: 300
                    },
                    '.fc-add-to-cart-button .fc-button-label': {
                        textTransform: 'none',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: '300'
                    },
                    '.fc-display-image-counter': {
                        background: 'gray.700'
                    }
                }}
            >
                <CbDesktopSpinner />
                <CbMobileSpinner />
            </Box>

            <Helmet>
                {/* Required dependency for external Fluid script */}
                <script src="https://code.jquery.com/jquery-3.6.0.min.js" crossOrigin="anonymous" />

                {/* Preload external scripts for faster execution */}
                <link rel="preload" as="script" href={configureHtmlJs} crossOrigin="anonymous" />
                <link rel="preload" as="script" href={configureUiJs} crossOrigin="anonymous" />
                <title>
                    {formatMessage({
                        id: 'cb.pdp.title',
                        defaultMessage: 'Custom Jewelry | Design Your Own Jewelry | Kendra Scott'
                    })}
                </title>
                <meta
                    name="description"
                    content={formatMessage({
                        id: 'cb.pdp.description',
                        defaultMessage:
                            'Want that custom jewelry look? Design your own jewelry at the Kendra Scott Color Bar™. Use our tool to create your own necklace, earrings, bracelet or rings.'
                    })}
                />
                {/* Load Fluid Configure scripts */}
                <script src={configureHtmlJs} crossOrigin="anonymous" />
                <script src={configureUiJs} crossOrigin="anonymous" />
            </Helmet>

            <Box className="mt-colorbar-products" id="mt-colorbar-products"></Box>
            {isLoading && !page && <EspSkeleton />}
            {!isLoading && page && <PDComponent page={page} parent={true} />}
        </Box>
    )
}

ColorBarPDP.getTemplateName = () => 'ColorBarPDP'

export default ColorBarPDP
